/**
 * Types for the standalone single SEG-Y file viewer (no registered prep run).
 */

/** Highpass / lowpass phase mode applied when filtering a gather. */
export type FilterPhase = "zero" | "min";

/** Render metadata returned alongside a rasterized gather image. */
export interface GatherMeta {
  clip_value: number;
  vmin: number;
  vmax: number;
  n_traces: number;
  ns: number;
  dt: number;
  /** time (s) of the first rendered sample (> 0 for a time-windowed gather) */
  t_min?: number;
  t_max: number;
  width: number;
  height: number;
  decim_traces: number;
  decim_time: number;
}

export interface GatherResponse {
  /** base64-encoded PNG */
  image: string;
  meta: GatherMeta;
}

/** A gather's samples for the client-side cell / wiggle display
 * (`/segy/gather_samples/`). `data` is trace-major `[width][height]`;
 * amplitude = `data[i] * meta.scale`. `width` / `height` may be decimated
 * (traces strided by `decim_traces`, time block-averaged by `decim_time`). */
export interface GatherSamples {
  meta: GatherMeta & { scale: number };
  data: Int16Array;
}

export interface RawHeadersResponse {
  /** decoded 3200-byte EBCDIC/ASCII text header */
  text_header: string;
  /** full rev1 binary-header dump, field name -> value */
  binary_header: { [field: string]: number };
  [key: string]: any;
}

export interface RawTraceHeadersResponse {
  total_traces: number;
  offset: number;
  limit: number;
  /** the fields materialized in `rows` (parallel order) */
  columns: string[];
  rows: number[][];
  /** every standard rev1 field name, in byte order (for the column chooser) */
  available_columns?: string[];
  /** field name -> 1-based inclusive [start, end] byte range */
  field_bytes?: { [name: string]: [number, number] };
  /** the default subset shown when no explicit selection is requested */
  default_columns?: string[];
}

export interface RawGatherParams {
  ffid?: number;
  trace_start?: number;
  trace_count?: number;
  clip?: number;
  min_val?: number;
  max_val?: number;
  lowcut?: number;
  low_roll?: number;
  highcut?: number;
  high_roll?: number;
}

/** One standard rev1 trace-header field, from the file-info catalog. */
export interface SegyHeaderField {
  name: string;
  /** 1-based inclusive [start, end] byte range */
  bytes: [number, number];
  /** "int16" / "int32" */
  format: string;
  description: string;
  /** the scalar applied to it in real units ("scalel" / "scalco"), if any */
  scalar: string | null;
}

/** Header-derived metadata of one raw SEG-Y file (from /api/v1/segy/info/). */
export interface SegyFileInfo {
  path: string;
  is_aws: boolean;
  size: number;
  ns: number;
  dt: number | null;
  format: number;
  n_traces: number | null;
  /** record length (s): (ns - 1) * dt */
  total_time?: number | null;
  /** every standard trace-header field — the catalog behind every picker */
  header_fields?: SegyHeaderField[];
  /** the fields a header scan always reads in its single pass */
  scan_fields?: string[];
}

/** One entry of a header-field picker: a standard field, a custom
 * byte/format field, or a pseudo-field such as the trace number. */
export interface SegyFieldOption {
  value: string;
  label: string;
  bytes?: [number, number] | null;
  description?: string;
  group: "common" | "custom" | "standard" | "special";
}

/** Fields listed first in every picker: the ones you reach for to address
 * gathers and inspect geometry. */
export const SEGY_COMMON_FIELDS = [
  "ffid",
  "chan",
  "source",
  "cdp",
  "offset",
  "soux",
  "souy",
  "recx",
  "recy",
  "souelev",
  "soudepth",
  "recelev",
  "cdpx",
  "cdpy",
  "iline",
  "xline",
  "shotpoint",
];

/** Single sampled header scan feeding the geometry map + header plots
 * (from /api/v1/segy/scan/). */
export interface SegyScanResponse {
  /** 1-based trace numbers of the sampled traces */
  trace: number[];
  fields: { [name: string]: number[] };
  columns: string[];
  /** fields returned in real units (scalel / scalco applied) */
  scaled_fields: string[];
  n_traces: number;
  scanned_traces: number;
  sampled: boolean;
  /** every `step`-th trace was read */
  step: number;
}

/** Header values of every trace in the displayed gather
 * (from /api/v1/segy/gather_headers/). */
export interface SegyGatherHeadersResponse {
  /** 1-based absolute trace numbers, in gather order */
  trace: number[];
  columns: string[];
  rows: number[][];
  field_bytes: { [name: string]: [number, number] };
}

/** A model grid box in the preprep / brute_stack convention: node (i, j)
 * sits at origin + R(angle) · (i·dx, j·dx) / xyscale, with i < nx1 along the
 * NX1 axis and j < nx2 along NX2; `angle` is degrees counter-clockwise from
 * +X (east). */
export interface SegyGridBox {
  xOrigin: number | null;
  yOrigin: number | null;
  nx1: number | null;
  nx2: number | null;
  dx: number | null;
  angle: number;
  /** model units per coordinate unit (preprep XYSCALE, e.g. 0.3048 for ft) */
  xyscale: number;
}

/** A SEG-Y file entry returned by the directory lookup. */
export interface SegyDirFile {
  name: string;
  size: number;
}

/** Result of /api/v1/segy/listdir/: the sub-folders and SEG-Y files of a
 * directory, for finding a raw input without typing its full path. */
export interface SegyDirListing {
  path: string;
  is_aws: boolean;
  dirs: string[];
  files: SegyDirFile[];
}

/** Gather/spectrum addressing + filter params for /api/v1/segy/{gather,spectrum}/. */
export interface SegyGatherParams {
  /** group by an arbitrary trace-header field (e.g. "source") */
  scan_field?: string;
  scan_value?: number;
  /** time window (s) shown and used for the spectrum */
  t_min?: number;
  t_max?: number;
  /** fixed amplitude limits (override the clip percentile) */
  min_val?: number;
  max_val?: number;
  /** shorthand for scan_field=ffid */
  ffid?: number;
  trace_start?: number;
  trace_count?: number;
  clip?: number;
  lowcut?: number;
  /** highpass rolloff slope in dB/octave */
  low_roll?: number;
  /** highpass phase mode (zero-phase vs minimum-phase) */
  low_phase?: FilterPhase;
  highcut?: number;
  /** lowpass rolloff slope in dB/octave */
  high_roll?: number;
  /** lowpass phase mode (zero-phase vs minimum-phase) */
  high_phase?: FilterPhase;
}

/** A user-defined trace-header field read at an arbitrary 1-based byte with a
 * chosen numeric format — for plotting / inspecting non-standard headers. */
export interface CustomHeaderField {
  /** 1-based byte position within the 240-byte trace header */
  byte: number;
  /** one of {@link SEGY_HEADER_FORMATS} (int16/uint16/int32/uint32/...) */
  format: string;
}

/** Numeric formats accepted for a custom header field (mirror the backend
 * HEADER_FIELD_FORMATS); label is what the picker shows. */
export const SEGY_HEADER_FORMATS: { value: string; label: string }[] = [
  { value: "int32", label: "int32 (4-byte signed)" },
  { value: "uint32", label: "uint32 (4-byte unsigned)" },
  { value: "int16", label: "int16 (2-byte signed)" },
  { value: "uint16", label: "uint16 (2-byte unsigned)" },
  { value: "int8", label: "int8 (1-byte signed)" },
  { value: "uint8", label: "uint8 (1-byte unsigned)" },
  { value: "ieee", label: "IEEE float (4-byte)" },
];

/** Backend field name for a custom field (matches segy_dtypes.custom_field_name);
 * the client builds it to find the field in a scan / trace-header response. */
export function customHeaderFieldName(f: CustomHeaderField): string {
  return `b${f.byte}_${f.format}`;
}

/** Request token for a custom field (`byte:format`), as the API expects. */
export function customHeaderFieldToken(f: CustomHeaderField): string {
  return `${f.byte}:${f.format}`;
}

/** Average amplitude spectrum of the addressed gather (raw + filtered). */
export interface SegySpectrumResponse {
  freq: number[];
  raw: number[];
  filtered: number[] | null;
  df: number;
  ns: number;
  file_dt?: number | null;
}

/** Picker settings of /api/v1/segy/automute/ (the top of scprep's auto
 * design gate). */
export interface SegyAutomuteParams {
  /** top relative to the first-break picks (ms; negative = above) */
  top_shift_ms?: number;
  fb_window_ms?: number;
  fb_max_s?: number;
  n_offset_bins?: number;
}

/** A straight-line mute t = start + growth · |offset| (growth in s per
 * offset unit). */
export interface SegyMuteFit {
  start_s: number;
  growth_s_per_unit: number;
}

/** Auto-picked top mute of a gather: per-trace curve in gather order (null =
 * unpicked) and its straight-line fit. */
export interface SegyAutomuteResponse {
  offset: number[];
  first_breaks: (number | null)[];
  top: (number | null)[];
  top_fit: SegyMuteFit | null;
  params: SegyAutomuteParams;
  n_shots: number;
}

/** The S3 buckets available for browsing + the custom-field formats the API
 * accepts (from /api/v1/segy/buckets/). */
export interface SegyBucketsResponse {
  buckets: string[];
  formats: string[];
}

/** Affine (inline/crossline) or principal-axis grid fit of a model volume. */
export interface SegyModelGrid {
  method: string;
  /** rotation / azimuth in degrees clockwise from north */
  rotation: number;
  /** affine fit only ↓ */
  origin?: [number, number];
  corners?: [number, number][];
  bin_inline?: number;
  bin_crossline?: number;
  inline_azimuth?: number;
  crossline_azimuth?: number;
  inline_range?: [number, number];
  crossline_range?: [number, number];
  inline_increment?: number;
  crossline_increment?: number;
  n_inlines?: number;
  n_crosslines?: number;
  /** principal-axis fallback only ↓ */
  major_axis_length?: number;
  minor_axis_length?: number;
}

/** Survey geometry of a 3D SEG-Y model / volume (from /api/v1/segy/model_geometry/). */
export interface SegyModelGeometryResponse {
  path: string;
  n_traces: number;
  scanned_traces: number;
  sampled: boolean;
  /** which header carried the coordinates ("cdp …" or "source …") */
  coordinate_source: string;
  has_coordinates: boolean;
  has_inline_crossline: boolean;
  bbox: {
    min_x: number;
    max_x: number;
    min_y: number;
    max_y: number;
    width: number;
    height: number;
  } | null;
  grid: SegyModelGrid | null;
  z_axis: { n_samples: number; interval: number | null; total: number | null };
  format: number;
}
